// ⋮ on a BLE lamp group's desktop room card: Rename, Delete group.
//
// The group's own screen has these for its CHILD groups and lamps, but a
// group in a room had no way to be renamed or removed from the card — and an
// empty test group could not be got rid of at all.
//
// Delete: owner only. A group WITH lamps is deleted with them — each lamp row
// first (the server refuses a group that still has children), then the group.
// ⚠️ From the desktop the lamps are NOT reset (no Bluetooth; the hub never
// holds a device key): they leave the app, and are reset at the wall before
// they are paired again. A group holding other GROUPS still has to be emptied
// first — deleting those is a choice, not a side effect.

import React, { useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShareQrDialog from '../ShareQrDialog';
import { useApp } from '../../../core/state/AppContext';
import { deleteBleNode, getBleNode, updateBleNode } from '../../../core/api/bleNodes';
import { isNativePlatform } from '../../../core/platform';
import { FIT_XS } from '../fitDialog';

/** Why this group cannot be deleted, or '' when it can. */
export function deleteBlocker(info) {
  if (!info) return 'Loading…';
  if (Number(info.node?.IsMaster) !== 1) return 'Only the owner can delete it';
  const groups = (info.children || []).filter((c) => c.NodeType !== 'lamp').length;
  if (groups > 0) return `Has ${groups} group${groups === 1 ? '' : 's'} — move or delete them first`;
  return '';
}

/** The lamps directly in this group — the rows deleted with it. */
export const lampCount = (info) => (info?.children || []).filter((c) => c.NodeType === 'lamp').length;

export default function BleGroupMenu({
  device, name, onRenamed, onDeleted, sx,
}) {
  const { userId } = useApp();
  const [anchor, setAnchor] = useState(null);
  const [info, setInfo] = useState(null); // getBleNode reply: { node, lamps, children }
  const [dialog, setDialog] = useState(null); // 'rename' | 'delete' | 'share'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const open = (e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
    setInfo(null);
    // Fresh every time: the lamp count decides whether Delete is allowed.
    getBleNode(userId, device.UniqueNumber)
      .then(setInfo)
      .catch((err) => setInfo({ error: err?.message || 'Could not load this group' }));
  };
  const close = () => { setDialog(null); setError(''); setBusy(false); };

  const save = async () => {
    const clean = text.trim();
    if (!clean || !info?.node) return;
    setBusy(true);
    setError('');
    try {
      const res = await updateBleNode(userId, info.node.NodeId, { Name: clean });
      if (res && String(res.status) === '500') throw new Error(res.message || 'Could not rename');
      onRenamed?.(clean);
      close();
    } catch (e) {
      setError(e?.message || 'Could not rename');
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!info?.node) return;
    setBusy(true);
    setError('');
    try {
      const lampRows = (info.children || []).filter((c) => c.NodeType === 'lamp');
      for (const l of lampRows) {
        // eslint-disable-next-line no-await-in-loop
        await deleteBleNode(userId, l.NodeId);
      }
      const res = await deleteBleNode(userId, info.node.NodeId, { isDevice: true });
      if (res && String(res.status) === '500') throw new Error(res.message || 'Could not delete');
      close();
      onDeleted?.();
    } catch (e) {
      setError(e?.message || 'Could not delete');
      setBusy(false);
    }
  };

  const blocker = info?.error ? info.error : deleteBlocker(info);
  const label = name || device.DeviceName || device.UniqueNumber;

  return (
    <>
      <IconButton size="small" onClick={open} sx={sx} aria-label="Group options">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
        <MenuItem
          disabled={!info?.node}
          onClick={() => { setAnchor(null); setText(label); setDialog('rename'); }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        {/* Sharing the group shares its lamps (access by ancestry, bleNodes.js). */}
        <MenuItem onClick={() => { setAnchor(null); setDialog('share'); }}>
          <ListItemIcon><QrCode2Icon fontSize="small" /></ListItemIcon>
          <ListItemText>Share (QR)</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!!blocker}
          onClick={() => { setAnchor(null); setDialog('delete'); }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color={blocker ? 'inherit' : 'error'} /></ListItemIcon>
          <ListItemText
            primary={lampCount(info) > 0 ? `Delete group and its ${lampCount(info)} lamp${lampCount(info) === 1 ? '' : 's'}` : 'Delete group'}
            secondary={blocker || undefined}
          />
        </MenuItem>
      </Menu>

      {/* A portal still bubbles through the React tree — keep its clicks off
          the card underneath, as the dialogs below do. */}
      <span onClick={(e) => e.stopPropagation()}>
        <ShareQrDialog
          open={dialog === 'share'}
          onClose={close}
          target={{ scope: 'device', uniqueNumber: device.UniqueNumber }}
          name={label}
          ble
        />
      </span>

      <Dialog open={dialog === 'rename'} onClose={busy ? undefined : close} {...FIT_XS} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Rename group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            inputProps={{ maxLength: 60 }}
          />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy || !text.trim() || text.trim() === label}>
            {busy ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'delete'} onClose={busy ? undefined : close} {...FIT_XS} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>{`Delete ${label}?`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {lampCount(info) > 0
              ? `Its ${lampCount(info)} lamp${lampCount(info) === 1 ? '' : 's'} are removed with it. The group leaves its room, and everyone it is shared with stops seeing it.`
              : 'The group has no lamps. It leaves its room, and everyone it is shared with stops seeing it.'}
          </Typography>
          {lampCount(info) > 0 && !isNativePlatform() && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              The lamps are removed from the app but NOT factory-reset. To reset them first (through the hub),
              cancel and use Remove all lamps on the group&apos;s Lamps ⋮ menu.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
